<template>
    <div v-show="ready" class="text-center preview-chart relative">
        <div class="absolute inset-0 flex items-center justify-center">
            <button type="button" class="btn btn-default" @click="openModal">
                {{ $t("Charts.PreviewButtonText") }}
            </button>
        </div>
        <div :style="previewStyle">
            <canvas :id="previewId" />
        </div>
    </div>

    <Dialog v-model:open="modalOpen">
        <DialogContent :id="modalId" class="md:max-w-4xl">
            <DialogHeader>
                {{ props.part.PartNumber }} <small>{{ props.part.Manufacturer }}</small>
            </DialogHeader>
            <div class="text-center">
                <h4 class="text-lg">{{ $t("Charts.Title") }}</h4>

                <PartChart
                    :part="props.part"
                    :part-key="props.partKey"
                    :preview-height="previewHeight"
                    :initial-chart-data="chartData" />
            </div>
            <DialogFooter>
                <DialogClose as-child>
                    <button type="button" class="btn btn-default">
                        {{ $t("Global.Close") }}
                    </button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import "chartjs-adapter-moment";
import {
    Chart,
    LineController,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Legend,
    Tooltip,
} from "chart.js";
Chart.register(LineController, TimeScale, LinearScale, PointElement, LineElement, Filler, Legend, Tooltip);

const { t } = useI18n();
const { gtag } = useGtag();

const props = defineProps({
    part: { type: Object, required: true },
    partKey: { type: String, required: true },
    initialChartData: { type: Object, default: () => null },
    previewHeight: { type: String, default: null },
    previewWidth: { type: String, default: null },
    loadChartData: { type: Boolean },
    previewIdPrefix: { type: String, default: "" },
});

const chartData = ref(props.initialChartData);
const quantity = ref<number | null>(null);
const modalOpen = ref(false);
const previewChart = ref<Chart | null>(null);

const previewId = computed(() => {
    return "preview-" + (props.previewIdPrefix || "") + props.partKey;
});

const modalId = computed(() => {
    return "chart-modal-" + props.partKey;
});

const ready = computed(() => {
    // I have data
    if (!chartData.value) {
        return false;
    }

    return true;
});

const previewStyle = computed(() => {
    return {
        width: props.previewWidth ? props.previewWidth + "px" : "",
        height: props.previewHeight ? props.previewHeight + "px" : "",
    };
});

onMounted(() => {
    if (props.loadChartData) {
        load(quantity.value);
    } else if (props.initialChartData) {
        generatePreview();
    } else {
        throw Error("You must either set loadChartData or initialChartData.");
    }
});

function generatePreview() {
    const ctx = document.getElementById(previewId.value) as HTMLCanvasElement;
    if (!ctx) {
        return;
    }

    quantity.value = chartData.value.Quantity;

    if (previewChart.value) {
        previewChart.value.destroy();
    }

    previewChart.value = new Chart(ctx, {
        type: "line",
        data: {
            labels: chartData.value.Dates,
            datasets: [
                {
                    yAxisID: "yStock",
                    label: t("Global.Stock"),
                    data: chartData.value.Stock,
                    borderColor: "rgb(23,172,235)",
                    borderWidth: 1,
                    backgroundColor: "rgba(23,172,235, .3)",
                    fill: true,
                },
                {
                    yAxisID: "yPrice",
                    label: t("Charts.Price"),
                    data: chartData.value.Price,
                    borderColor: "rgb(34,53,64)",
                    borderWidth: 1,
                    backgroundColor: "rgba(134,153,164, .3)",
                    fill: true,
                },
            ],
        },
        options: {
            maintainAspectRatio: false,
            responsive: true,
            elements: {
                point: {
                    radius: 0,
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    type: "time",
                    ticks: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawTicks: false,
                        drawOnChartArea: false,
                    },
                },
                yPrice: {
                    stacked: true,
                    beginAtZero: true,
                    display: false,
                    ticks: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawTicks: false,
                        drawOnChartArea: false,
                    },
                    title: {
                        text: t("Charts.Price"),
                    },
                },
                yStock: {
                    stacked: true,
                    beginAtZero: true,
                    display: false,
                    ticks: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawTicks: false,
                        drawOnChartArea: false,
                    },
                    title: {
                        display: false,
                    },
                },
            },
        },
    });
}

async function load(quantity: number | null) {
    // Don't make calls to the part-chart endpoint when we don't have sufficient part information
    if (!props.part.PartNumber || !props.part.ManufacturerId) {
        return;
    }

    const result = await axios.post("/api/part-chart", {
        Parts: [
            {
                PartNumber: props.part.PartNumber,
                ManufacturerId: props.part.ManufacturerId,
            },
        ],
        Quantity: quantity,
    });
    if (result && result.data && result.data.length) {
        chartData.value = result.data[0];
        generatePreview();
    }
}

function openModal() {
    modalOpen.value = true;
    trackClick(props.part.Manufacturer, props.part.PartNumber);
    gtag("event", "chart_open", {
        manufacturer_name: props.part.Manufacturer,
        part_number: props.part.PartNumber,
    });
}

function trackClick(action: string, label: string) {
    gtag("event", action, { event_category: "Chart", event_label: label });
}
</script>
